import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CompanyProfile, CompanyProfileState, PaymentLog, SuspiciousSurvey } from '@/types/company.types';

/** Payload for update-company requests: must include companyId plus any profile fields to change. */
export interface UpdateCompanyInfoPayload extends Partial<CompanyProfile> {
  companyId: string;
}

/** Payload shape shared by all subscription-mutation requests. */
export interface SubscriptionActionPayload {
  companyId: string;
  [key: string]: unknown;
}

/** Payload for the resetPilotData request. */
export interface ResetPilotDataPayload {
  companyId: string;
  resetThematic: boolean;
  resetImport: boolean;
}

const initialState: CompanyProfileState = {
  company: null,
  loading: false,
  error: null,
  paymentLogs: { list: [], loading: false, total: 0 },
  suspiciousSurveys: { list: [], loading: false },
  updateCompany: { loading: false, error: null },
};

const companyProfileSlice = createSlice({
  name: 'companyProfile',
  initialState,
  reducers: {
    // ── Fetch company info ──────────────────────────────────────────────
    fetchCompanyInfoRequest(state, _action: PayloadAction<string>) {
      state.loading = true;
      state.error = null;
      state.company = null;
    },
    fetchCompanyInfoSuccess(state, action: PayloadAction<CompanyProfile>) {
      state.loading = false;
      state.company = action.payload;
    },
    fetchCompanyInfoFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // ── Update company info (special access, etc.) ──────────────────────
    updateCompanyInfoRequest(state, _action: PayloadAction<UpdateCompanyInfoPayload>) {
      state.updateCompany = { loading: true, error: null };
    },
    updateCompanyInfoSuccess(state, action: PayloadAction<Partial<CompanyProfile>>) {
      state.updateCompany = { loading: false, error: null };
      if (state.company) {
        state.company = { ...state.company, ...action.payload };
      }
    },
    updateCompanyInfoFailure(state, action: PayloadAction<string>) {
      state.updateCompany = { loading: false, error: action.payload };
    },

    // ── Subscriptions ───────────────────────────────────────────────────
    addSubscriptionRequest(state, _action: PayloadAction<SubscriptionActionPayload>) {
      state.updateCompany = { loading: true, error: null };
    },
    addSubscriptionSuccess(state) {
      state.updateCompany = { loading: false, error: null };
    },
    addSubscriptionFailure(state, action: PayloadAction<string>) {
      state.updateCompany = { loading: false, error: action.payload };
    },

    editSubscriptionRequest(state, _action: PayloadAction<SubscriptionActionPayload>) {
      state.updateCompany = { loading: true, error: null };
    },
    editSubscriptionSuccess(state) {
      state.updateCompany = { loading: false, error: null };
    },
    editSubscriptionFailure(state, action: PayloadAction<string>) {
      state.updateCompany = { loading: false, error: action.payload };
    },

    updatePlanRequest(state, _action: PayloadAction<SubscriptionActionPayload>) {
      state.updateCompany = { loading: true, error: null };
    },
    updatePlanSuccess(state) {
      state.updateCompany = { loading: false, error: null };
    },
    updatePlanFailure(state, action: PayloadAction<string>) {
      state.updateCompany = { loading: false, error: action.payload };
    },

    purchaseAddonsRequest(state, _action: PayloadAction<SubscriptionActionPayload>) {
      state.updateCompany = { loading: true, error: null };
    },
    purchaseAddonsSuccess(state) {
      state.updateCompany = { loading: false, error: null };
    },
    purchaseAddonsFailure(state, action: PayloadAction<string>) {
      state.updateCompany = { loading: false, error: action.payload };
    },

    /** Generic failure kept for backward-compat; the specific failures above are preferred. */
    subscriptionActionFailure(state, action: PayloadAction<string>) {
      state.updateCompany = { loading: false, error: action.payload };
    },

    // ── Payment logs ────────────────────────────────────────────────────
    fetchPaymentLogsRequest(state, _action: PayloadAction<string>) {
      state.paymentLogs.loading = true;
    },
    fetchPaymentLogsSuccess(state, action: PayloadAction<{ list: PaymentLog[]; total: number }>) {
      state.paymentLogs = { ...action.payload, loading: false };
    },
    fetchPaymentLogsFailure(state) {
      state.paymentLogs.loading = false;
    },

    // ── Suspicious surveys ──────────────────────────────────────────────
    fetchSuspiciousSurveysRequest(state, _action: PayloadAction<string>) {
      state.suspiciousSurveys.loading = true;
    },
    fetchSuspiciousSurveysSuccess(state, action: PayloadAction<SuspiciousSurvey[]>) {
      state.suspiciousSurveys = { list: action.payload, loading: false };
    },
    unblockSurveyRequest(_state, _action: PayloadAction<{ surveyId: string; companyId: string }>) {},
    unblockSurveySuccess(state, action: PayloadAction<string>) {
      state.suspiciousSurveys.list = state.suspiciousSurveys.list.filter(
        (s) => s._id !== action.payload,
      );
    },

    // ── Admin actions ───────────────────────────────────────────────────
    clearCacheRequest(_state, _action: PayloadAction<string>) {},
    clearCacheSuccess(_state) {},
    clearCacheFailure(_state, _action: PayloadAction<string>) {},

    resetGatewayRequest(_state, _action: PayloadAction<string>) {},
    resetGatewaySuccess(_state) {},
    resetGatewayFailure(_state, _action: PayloadAction<string>) {},

    accountReviewRequest(_state, _action: PayloadAction<string>) {},
    accountReviewSuccess(_state) {},
    accountReviewFailure(_state, _action: PayloadAction<string>) {},

    resetPilotDataRequest(_state, _action: PayloadAction<ResetPilotDataPayload>) {},
    resetPilotDataSuccess(_state) {},
    resetPilotDataFailure(_state, _action: PayloadAction<string>) {},

    // ── Reset ───────────────────────────────────────────────────────────
    clearCompanyProfile(state) {
      Object.assign(state, initialState);
    },
  },
});

export const {
  fetchCompanyInfoRequest, fetchCompanyInfoSuccess, fetchCompanyInfoFailure,
  updateCompanyInfoRequest, updateCompanyInfoSuccess, updateCompanyInfoFailure,
  addSubscriptionRequest, addSubscriptionSuccess, addSubscriptionFailure,
  editSubscriptionRequest, editSubscriptionSuccess, editSubscriptionFailure,
  updatePlanRequest, updatePlanSuccess, updatePlanFailure,
  purchaseAddonsRequest, purchaseAddonsSuccess, purchaseAddonsFailure,
  subscriptionActionFailure,
  fetchPaymentLogsRequest, fetchPaymentLogsSuccess, fetchPaymentLogsFailure,
  fetchSuspiciousSurveysRequest, fetchSuspiciousSurveysSuccess,
  unblockSurveyRequest, unblockSurveySuccess,
  clearCacheRequest, clearCacheSuccess, clearCacheFailure,
  resetGatewayRequest, resetGatewaySuccess, resetGatewayFailure,
  accountReviewRequest, accountReviewSuccess, accountReviewFailure,
  resetPilotDataRequest, resetPilotDataSuccess, resetPilotDataFailure,
  clearCompanyProfile,
} = companyProfileSlice.actions;

export default companyProfileSlice.reducer;
